import type { Brand, Campaign, CommunityComment, CommunityPost, CommunityReaction, MenuData, Notification, SiteConfig, SubmissionData, User, WorkflowLog, Aggregator } from '../types';
import { canonicalizePermissions } from '../utils/permissions';
import { mergeAggregatorProfiles, saveProfile as saveAggregatorProfile } from './aggregatorImageProfile';
import { getToken, setToken, removeToken } from '../utils/auth';

const DEFAULT_BASE = '/api';
const rawBase = ((import.meta as any).env?.VITE_API_BASE_URL ?? DEFAULT_BASE).replace(/\/$/, '');
let API_BASE_URL = rawBase;
if (typeof window !== 'undefined') {
  // If a hard-coded absolute base points to a different origin, prefer the current origin to avoid cross-origin prod/dev mixups.
  if (/^https?:\/\//i.test(rawBase)) {
    const envOrigin = new URL(rawBase).origin;
    if (envOrigin !== window.location.origin) {
      API_BASE_URL = window.location.origin + (new URL(rawBase).pathname || '/api');
    }
  } else {
    API_BASE_URL = rawBase;
  }
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions<TBody = unknown> {
  method?: HttpMethod;
  body?: TBody;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  auth?: boolean;
}

async function request<TResponse, TBody = unknown>(path: string, options: RequestOptions<TBody> = {}): Promise<TResponse> {
  const { method = 'GET', body, headers = {}, signal, auth = true } = options;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const token = auth ? getToken() : null;
  const response = await fetch(`${API_BASE_URL}${normalizedPath}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API ${method} ${normalizedPath} failed: ${response.status} ${response.statusText} - ${errorText}`.trim());
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return (await response.json()) as TResponse;
  }

  return (await response.text()) as unknown as TResponse;
}

const normalizeArray = <T>(value: any): T[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [value];
};

const normalizeSubmission = (s: any): SubmissionData => {
  if (!s || typeof s !== 'object') return { brands: [], items: [], manager: { name: '', fileNumber: '' } };
  return {
    ...s,
    brands: normalizeArray<string>((s as any).brands),
    items: normalizeArray((s as any).items),
    currentStage: s.currentStage || (s.status === 'Needs Revision' ? 'Returned' : 'Food & Beverage'),
  } as SubmissionData;
};

const normalizeCampaign = (c: any): Campaign => {
  const base = (c || {}) as Campaign;
  return {
    ...base,
    brandIds: normalizeArray<string>((c as any).brandIds),
    brands: normalizeArray((c as any).brands),
    aggregators: normalizeArray((c as any).aggregators),
  } as Campaign;
};

export const authApi = {
  login: async (username: string, password: string) => {
    const res = await request<{ token: string; user: User }>('/auth/login', { method: 'POST', body: { username, password }, auth: false });
    setToken(res.token);
    return { ...res, user: { ...res.user, permissions: canonicalizePermissions((res.user as any)?.permissions) } };
  },
  register: (payload: Partial<User> & { password: string }) =>
    request<{ id: string; pending: boolean }>('/auth/register', { method: 'POST', body: payload, auth: false }),
  me: async () => {
    const res = await request<{ user: User }>('/auth/me');
    return { ...res, user: { ...res.user, permissions: canonicalizePermissions((res.user as any)?.permissions) } };
  },
  logout: async () => {
    await request<void>('/auth/logout', { method: 'POST' });
    removeToken();
  },
};

export const campaignsApi = {
  list: async (): Promise<Campaign[]> => {
    try {
      const res = await request<Campaign[]>('/campaigns', { auth: true });
      return (res || []).map(normalizeCampaign);
    } catch (err) {
      console.warn('campaigns list failed, returning empty', err);
      return [];
    }
  },
  create: (payload: Campaign): Promise<Campaign[]> => request<Campaign[]>('/campaigns', { method: 'POST', body: payload }),
  update: (id: string, payload: Campaign): Promise<Campaign[]> =>
    request<Campaign[]>(`/campaigns/${encodeURIComponent(id)}`, { method: 'PUT', body: payload }),
  delete: (id: string): Promise<Campaign[]> =>
    request<Campaign[]>(`/campaigns/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  getBrands: (id: string): Promise<{ campaignId: string; brands: { id: string; name: string }[] }> =>
    request(`/campaigns/${encodeURIComponent(id)}/brands`),
};

export const campaignItemsApi = {
  list: (campaignId: string) =>
    request(`/campaigns/${encodeURIComponent(campaignId)}/items`),
  add: (campaignId: string, items: any[]) =>
    request(`/campaigns/${encodeURIComponent(campaignId)}/items`, { method: 'POST', body: { items } }),
  delete: (campaignId: string, itemId: string) =>
    request(`/campaigns/${encodeURIComponent(campaignId)}/items/${encodeURIComponent(itemId)}`, { method: 'DELETE' }),
};

export const brandsApi = {
  list: (): Promise<Brand[]> => request<Brand[]>('/brands', { auth: false }),
  upsert: (payload: { id?: string; name: string; logo?: string | null }): Promise<Brand[]> =>
    request<Brand[]>('/brands', { method: 'POST', body: payload }),
};

export const siteConfigApi = {
  get: (): Promise<SiteConfig> => request<SiteConfig>('/site-config', { auth: false }),
  update: (payload: Partial<SiteConfig>) => request<SiteConfig>('/site-config', { method: 'PUT', body: payload }),
};

export const submissionsApi = {
  list: async (params?: { managerId?: string; limit?: number; offset?: number; status?: string; campaignId?: string; q?: string }): Promise<{ items: SubmissionData[]; page: { limit: number; offset: number; total: number } }> => {
    const search = params?.managerId ? `?managerId=${encodeURIComponent(params.managerId)}` : '';
    const qs = new URLSearchParams();
    if (params?.managerId) qs.set('managerId', params.managerId);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    if (params?.status) qs.set('status', params.status);
    if (params?.campaignId) qs.set('campaignId', params.campaignId);
    if (params?.q) qs.set('q', params.q);
    const query = qs.toString();
    const res = await request<any>(`/submissions${query ? `?${query}` : ''}`);
    const itemsRaw = Array.isArray(res) ? res : res?.items;
    const page = res?.page || { limit: params?.limit ?? (itemsRaw?.length ?? 0), offset: params?.offset ?? 0, total: Array.isArray(itemsRaw) ? itemsRaw.length : 0 };
    return { items: normalizeArray(itemsRaw).map(normalizeSubmission), page };
  },
  create: async (payload: SubmissionData): Promise<SubmissionData> =>
    normalizeSubmission(await request<SubmissionData>('/submissions', {
      method: 'POST',
      body: payload,
    })),
  update: async (id: string, payload: SubmissionData): Promise<SubmissionData> =>
    normalizeSubmission(await request<SubmissionData>(`/submissions/${encodeURIComponent(id)}`, { method: 'PUT', body: payload })),
  updateStatus: async (submissionId: string, payload: { status: string; currentStage?: string; workflowLog?: any }) =>
    normalizeSubmission(await request<SubmissionData>(`/submissions/${encodeURIComponent(submissionId)}/status`, {
      method: 'PATCH',
      body: payload,
    })),
  startBundle: (campaignId: string) =>
    request(`/submissions/start-bundle`, {
      method: 'POST',
      body: { campaignId },
    }),
  comment: (submissionId: string, payload: { comment: string; stage: string; user: string; attachments?: any[] }) =>
    request<WorkflowLog>(`/submissions/${encodeURIComponent(submissionId)}/comment`, {
      method: 'POST',
      body: payload,
    }),
  logs: (submissionId: string) => request<WorkflowLog[]>(`/submissions/${encodeURIComponent(submissionId)}/logs`),
};

export const notificationsApi = {
  list: (): Promise<Notification[]> => request<Notification[]>('/notifications'),
  create: (payload: { type: string; title: string; message: string; targetView?: string; targetId?: string }) =>
    request<Notification[]>('/notifications', { method: 'POST', body: payload }),
  markRead: (): Promise<Notification[]> => request<Notification[]>('/notifications/read', { method: 'POST' }),
  clear: (): Promise<Notification[]> => request<Notification[]>('/notifications/clear', { method: 'POST' }),
};

export const activityApi = {
  list: () => request('/activity'),
  clear: () => request('/activity', { method: 'DELETE' }),
};

export const usersApi = {
  list: (): Promise<User[]> => request<User[]>('/users'),
  get: (id: string): Promise<User> => request<User>(`/users/${encodeURIComponent(id)}`),
  update: (id: string, payload: Partial<User> & { password?: string }) =>
    request(`/users/${encodeURIComponent(id)}`, { method: 'PUT', body: payload }),
};

export const aggregatorsApi = {
  list: async (includeInactive = false): Promise<Aggregator[]> => {
    const res = await request<Aggregator[]>(`/aggregators${includeInactive ? '?includeInactive=true' : ''}`);
    return mergeAggregatorProfiles(res || []);
  },
  create: async (payload: { name: string; active?: boolean; imageProfile?: any }): Promise<Aggregator[]> => {
    const res = await request<Aggregator[]>('/aggregators', { method: 'POST', body: payload });
    if (payload.imageProfile && Array.isArray(res)) {
      const created = res.find((a) => a.name === payload.name);
      if (created?.id) saveAggregatorProfile(created.id, payload.imageProfile);
    }
    return mergeAggregatorProfiles(res || []);
  },
  update: async (id: string, payload: { name: string; active?: boolean; imageProfile?: any }): Promise<Aggregator[]> => {
    const res = await request<Aggregator[]>(`/aggregators/${encodeURIComponent(id)}`, { method: 'PUT', body: payload });
    if (payload.imageProfile) saveAggregatorProfile(id, payload.imageProfile);
    return mergeAggregatorProfiles(res || []);
  },
  delete: async (id: string) => {
    const res = await request<Aggregator[]>(`/aggregators/${encodeURIComponent(id)}`, { method: 'DELETE' });
    return Array.isArray(res) ? mergeAggregatorProfiles(res) : res;
  },
};

export const analyticsApi = {
  summary: (params: { brandIds?: string[] } = {}) => {
    const search = new URLSearchParams();
    if (params.brandIds && params.brandIds.length) {
      search.set('brandIds', params.brandIds.join(','));
    }
    const qs = search.toString();
    return request(`/analytics/summary${qs ? `?${qs}` : ''}`);
  },
};

export const tablePreferencesApi = {
  get: (viewKey: string): Promise<{ columnsConfig: { key: string; visible: boolean }[] } | null> =>
    request(`/table-preferences?view=${encodeURIComponent(viewKey)}`),
  save: (viewKey: string, columnsConfig: { key: string; visible: boolean }[]) =>
    request(`/table-preferences`, { method: 'PUT', body: { viewKey, columnsConfig } }),
};

export const activityLogApi = {
  list: (params: Record<string, string | number | undefined> = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) search.set(k, String(v));
    });
    const qs = search.toString();
    return request(`/activity-log${qs ? `?${qs}` : ''}`);
  },
  create: (payload: { eventType: string; context?: string; payload?: any }) =>
    request(`/activity-log`, { method: 'POST', body: payload }),
};

export const communityApi = {
  listPosts: (params: { cursor?: string; limit?: number } = {}): Promise<{ items: CommunityPost[]; cursor?: string | null }> => {
    const search = new URLSearchParams();
    if (params.cursor) search.set('cursor', params.cursor);
    if (params.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    return request(`/community/posts${qs ? `?${qs}` : ''}`);
  },
  createPost: (payload: Partial<CommunityPost>): Promise<CommunityPost> =>
    request(`/community/posts`, { method: 'POST', body: payload }),
  updatePost: (id: string, payload: Partial<CommunityPost>): Promise<CommunityPost> =>
    request(`/community/posts/${encodeURIComponent(id)}`, { method: 'PUT', body: payload }),
  deletePost: (id: string): Promise<void> =>
    request(`/community/posts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  addComment: (postId: string, payload: { body: string }): Promise<CommunityComment> =>
    request(`/community/posts/${encodeURIComponent(postId)}/comments`, { method: 'POST', body: payload }),
  listComments: (postId: string, params: { cursor?: string; limit?: number } = {}): Promise<{ items: CommunityComment[]; cursor?: string | null }> => {
    const search = new URLSearchParams();
    if (params.cursor) search.set('cursor', params.cursor);
    if (params.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    return request(`/community/posts/${encodeURIComponent(postId)}/comments${qs ? `?${qs}` : ''}`);
  },
  addFeedback: (postId: string, payload: { message: string }): Promise<CommunityReaction> =>
    request(`/community/posts/${encodeURIComponent(postId)}/reactions`, { method: 'POST', body: payload }),
  listFeedback: (postId: string): Promise<{ items: CommunityReaction[] }> =>
    request(`/community/posts/${encodeURIComponent(postId)}/reactions`),
};

export interface MenuBuilderMenuResponse {
  menu: MenuData | null;
}

export const menuBuilderApi = {
  getMenu: async (userId?: string, menuId?: string): Promise<MenuData> => {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (menuId) params.set('menuId', menuId);
    const qs = params.toString();
    const res = await request<MenuBuilderMenuResponse>(`/menu-builder${qs ? `?${qs}` : ''}`);
    return res?.menu ?? { title: { en: 'New Menu' }, categories: [] };
  },
  listMenus: async (userId?: string): Promise<{ id: string; title_en: string; title_ar?: string; created_at: string; updated_at: string }[]> => {
    const qs = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    const res = await request<{ menus: any[] }>(`/menu-builder/list${qs}`);
    return res.menus;
  },
  saveMenu: (userId: string | undefined, menu: MenuData): Promise<MenuBuilderMenuResponse> =>
    request<MenuBuilderMenuResponse>('/menu-builder', { method: 'POST', body: { userId, menu, id: menu.id } }),
  copyMenu: (sourceMenuId: string): Promise<{ success: boolean; newMenuId: string }> =>
    request('/menu-builder/copy', { method: 'POST', body: { sourceMenuId } }),
  deleteMenu: (id: string): Promise<{ success: boolean }> =>
    request(`/menu-builder/delete?id=${encodeURIComponent(id)}`, { method: 'POST' }),

  // Legacy helpers used by earlier skeleton tabs
  load: (): Promise<MenuBuilderMenuResponse> => request<MenuBuilderMenuResponse>('/menu-builder'),
  save: (payload: {
    name?: string;
    categories: Array<{
      id?: string;
      name: string;
      sortOrder: number;
      items: Array<{ id?: string; name: string; price: number; description: string; sortOrder: number }>;
    }>;
  }): Promise<MenuBuilderMenuResponse> => {
    const menu: MenuData = {
      title: { en: payload.name || 'Default Menu' },
      categories: (payload.categories || []).map((cat) => ({
        id: cat.id || crypto.randomUUID(),
        name: { en: cat.name },
        items: (cat.items || []).map((item) => ({
          id: item.id || crypto.randomUUID(),
          name: { en: item.name },
          description: { en: item.description },
          price: item.price,
          choiceGroups: [],
        })),
      })),
    };
    return request<MenuBuilderMenuResponse>('/menu-builder', { method: 'POST', body: { menu } });
  },
};

export const apiClient = {
  auth: authApi,
  campaigns: campaignsApi,
  campaignItems: campaignItemsApi,
  brands: brandsApi,
  siteConfig: siteConfigApi,
  submissions: submissionsApi,
  notifications: notificationsApi,
  users: usersApi,
  analytics: analyticsApi,
  tablePreferences: tablePreferencesApi,
  activityLog: activityLogApi,
  activity: activityApi,
  aggregators: aggregatorsApi,
  community: communityApi,
  menuBuilder: menuBuilderApi,
  uploads: {
    uploadImage: (file: File) => {
      return fetch(`${API_BASE_URL}/uploads`, {
        method: 'POST',
        headers: {
          ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        },
        body: file,
      }).then(async (res) => {
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`Upload failed: ${res.status} ${txt}`);
        }
        return res.json();
      });
    },
  },
};
